import { useCallback, useState } from 'react';
import { debounce } from 'throttle-debounce';

import { ISuggestion } from '../../../packages/SuggestField';

export const DEFAULT_MIN_SEARCH_LENGTH = 2;

export interface IUseSuggestProps<T extends ISuggestion> {
  value: string;
  minSearchLength?: number;

  onItemClick(value: T): void;
  onChange(value: string): void;
  requestSuggestions(value: string): Promise<T[]>;
}
export const useSuggest = <T extends ISuggestion>(props: IUseSuggestProps<T>) => {
  const { requestSuggestions, onChange, onItemClick, minSearchLength = DEFAULT_MIN_SEARCH_LENGTH } = props;
  const [suggestions, setSuggestions] = useState<T[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isSuggestShown, setIsSuggestShown] = useState(false);

  const showSuggest = useCallback(() => setIsSuggestShown(true), []);
  const hideSuggest = useCallback(() => setIsSuggestShown(false), []);

  const _getSuggestions = useCallback(
    async (value: string) => {
      if (!value.length || value.length < minSearchLength) {
        return;
      }

      setIsFetching(true);

      const suggestions = await requestSuggestions(value);

      showSuggest();
      setSuggestions(suggestions);
      setIsFetching(false);
    },
    [showSuggest, requestSuggestions, minSearchLength],
  );

  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  const getSuggestions = useCallback(debounce(200, _getSuggestions), [_getSuggestions]);

  const onSuggestionValueChange = useCallback(
    (value: string) => {
      onChange(value);
      getSuggestions(value);
    },
    [getSuggestions, onChange],
  );

  const handleItemClick = useCallback(
    (value: T) => {
      onItemClick(value);
      hideSuggest();
    },
    [onItemClick, hideSuggest],
  );

  return {
    suggestions,
    getSuggestions,
    isFetching,
    onSuggestionValueChange,
    handleItemClick,
    isSuggestShown,
    hideSuggest,
    showSuggest,
  };
};
